import React, { useRef } from 'react';
import * as Sentry from '@sentry/react';
import { Popover, TextInput, useToast, VStack } from '@televet/kibble-ui';
import { SidePanelProfileClinicPetParentFragment, useUpdateOneClinicPetParentMutation } from 'shared/types/graphql';
import useKeypress from 'shared/hooks/useKeypress';
import { useForm } from 'react-hook-form';

interface EditClientNameForm {
  firstName: string;
  lastName: string;
}

interface IClinicPetParentNamePopoverProps {
  isOpen: boolean;
  onClose: () => void;
  clinicPetParent: SidePanelProfileClinicPetParentFragment;
  anchor: JSX.Element;
  onReloadClinicPetParent: (clinicPetParentId: string) => void;
}

const ClinicPetParentNamePopover = ({
  isOpen,
  clinicPetParent,
  anchor,
  onReloadClinicPetParent,
  onClose,
}: IClinicPetParentNamePopoverProps): JSX.Element => {
  const { register, handleSubmit } = useForm<EditClientNameForm>({
    defaultValues: { firstName: clinicPetParent.firstName, lastName: clinicPetParent.lastName },
  });

  const toast = useToast();

  const [updateClinicPetParentName, { loading: isUpdatingClinicPetParentName }] = useUpdateOneClinicPetParentMutation({
    onCompleted: () => {
      onClose();
      onReloadClinicPetParent(clinicPetParent.id);
    },
    onError: (error) => {
      console.error(error);
      Sentry.captureException(error);
      toast({
        status: 'error',
        description: "Whoops! An error occurred while updating the client's name. Please try again.",
      });
    },
  });

  const handleSave = ({ firstName, lastName }: EditClientNameForm): void => {
    updateClinicPetParentName({
      variables: {
        data: {
          firstName,
          lastName,
        },
        where: {
          id: clinicPetParent.id,
        },
      },
    });
  };

  const onSave = (): void => {
    handleSubmit(handleSave)();
  };

  useKeypress({
    key: 'Enter',
    handler: () => {
      onSave();
    },
    skip: !isOpen,
  });

  const firstNameInputRef = useRef<HTMLInputElement | null>(null);

  return (
    <Popover
      isOpen={isOpen}
      anchor={anchor}
      variant="default"
      placement="bottom"
      size="md"
      initialFocusRef={firstNameInputRef}
      headerProps={{ children: 'Edit Client Name', textAlign: 'left', size: 'sm' }}
      footerProps={{ submitText: 'Save', onSubmit: onSave, isLoading: isUpdatingClinicPetParentName }}
      onClose={onClose}
    >
      <VStack align="stretch" spacing={2}>
        <TextInput
          autoFocus
          name="firstName"
          label="First Name"
          ref={(el): void => {
            if (el) {
              firstNameInputRef.current = el;
              register(el);
            }
          }}
        />
        <TextInput name="lastName" label="Last Name" ref={register} />
      </VStack>
    </Popover>
  );
};

export default ClinicPetParentNamePopover;
